import { useState } from 'react';
import { DateTime } from 'luxon';

import { Button } from './Button';
import { DatePicker } from './DatePicker';
import { Modal } from './Modal';

interface InlineDateInputProps {
  /** yyyy-MM-dd */
  value?: string | null;
  onSave: (date: string) => Promise<unknown>;
  /** Modal title and date field label */
  label: string;
  placeholder?: string;
  disabled?: boolean;
}

/** Shows a date as text; clicking opens a modal to pick a date and confirm it */
export function InlineDateInput({ value, onSave, label, placeholder = `Not set`, disabled }: InlineDateInputProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string | null>(null);

  function openModal() {
    setDraft(value || null);
    setOpen(true);
  }

  async function save() {
    try {
      await onSave(draft);
      setOpen(false);
    }
    catch {
      // Error toast is shown by the API middleware, keep the modal open to retry
    }
  }

  return (
    <>
      <button
        type={ `button` }
        onClick={ openModal }
        disabled={ disabled }
        className={ value ?
          `cursor-pointer text-[0.8125rem] text-gray-900 hover:text-paidniceGreen-700 hover:underline disabled:cursor-not-allowed disabled:opacity-50` :
          `cursor-pointer text-[0.8125rem] text-gray-400 hover:text-paidniceGreen-700 hover:underline disabled:cursor-not-allowed disabled:opacity-50` }
      >
        { value ? DateTime.fromISO(value).toFormat(`d LLL yyyy`) : placeholder }
      </button>

      <Modal
        open={ open }
        setOpen={ setOpen }
        title={ label }
        className={ `sm:max-w-sm` }
      >
        <div className={ `space-y-3` }>
          <DatePicker
            label={ label }
            name={ `inlineDate` }
            value={ draft }
            onChange={ ({ value: date }) => setDraft(date) }
            displayValue={ draft ? DateTime.fromISO(draft).toFormat(`d LLL yyyy`) : null }
            disableClear
          />
          <div className={ `flex justify-end gap-2` }>
            <Button color={ `none` }
              onClick={ () => setOpen(false) }
              disabled={ disabled }>
              { `Cancel` }
            </Button>
            <Button onClick={ save }
              loading={ disabled }
              disabled={ !draft || draft === value }>
              { `Save` }
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
